import { supabase } from "@/lib/supabase";
import { getAdminClient } from "@/lib/supabaseAdmin";
import useAuthStore from "@/store/authStore";

/**
 * Creates a workspace invitation notification
 *
 * @param {string} recipientUserId - The user ID of the invitation recipient
 * @param {string} senderUserId - The user ID of the invitation sender
 * @param {object} workspaceData - Data about the workspace being shared
 * @returns {Promise<object>} - The created notification object
 */
export async function createWorkspaceInvitation(
  recipientUserId,
  senderUserId,
  workspaceData
) {
  try {
    // Validate input data
    if (
      !recipientUserId ||
      !senderUserId ||
      !workspaceData ||
      !workspaceData.id
    ) {
      throw new Error("Missing required parameters for workspace invitation");
    }

    // Get the authenticated user - critical for RLS policy
    const {
      data: { user: currentUser },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError) {
      console.error("Auth error:", authError);
      throw new Error("Authentication error: " + authError.message);
    }

    if (!currentUser) {
      throw new Error("You must be logged in to send invitations");
    }

    // Verify that the sender is the authenticated user
    if (senderUserId !== currentUser.id) {
      console.warn(
        `Auth mismatch! Current auth user (${currentUser.id}) doesn't match the provided sender ID (${senderUserId})`
      );
      throw new Error(
        "Authentication mismatch: Sender ID must match the currently authenticated user"
      );
    }

    // Ensure workspace_id is properly formatted as string
    const workspaceId = String(workspaceData.id);

    // Ensure both user IDs are properly formatted strings for UUID references
    const safeRecipientId = String(recipientUserId);
    const safeSenderId = String(senderUserId);

    // Format content for the notification with safe values
    const content = {
      workspace_id: workspaceId,
      workspace_name: String(workspaceData.name || ""),
      team_key: workspaceData.team_key ? String(workspaceData.team_key) : "",
      message: workspaceData.inviteMessage
        ? String(workspaceData.inviteMessage)
        : `You've been invited to join ${String(workspaceData.name || "a workspace")}`,
      action: "join_workspace",
    };

    // Verify the workspace exists first
    const { data: workspace, error: workspaceError } = await supabase
      .from("workspaces")
      .select("id, workspace_members, owner_id")
      .eq("id", workspaceId)
      .single();

    if (workspaceError) {
      console.error("Error fetching workspace:", workspaceError);
      throw new Error(`Workspace not found: ${workspaceId}`);
    }

    // Check if sender is in workspace_members
    const isWorkspaceMember =
      workspace.workspace_members &&
      workspace.workspace_members.includes(safeSenderId);

    // Check if sender is the owner
    const isWorkspaceOwner = workspace.owner_id === safeSenderId;

    if (!isWorkspaceMember && !isWorkspaceOwner) {
      throw new Error(
        "You must be a workspace member or owner to send invitations"
      );
    }

    // Create notification with proper field mapping:
    // user_id = recipient user (for RLS policy)
    // sender_id = actual sender
    // receiver_id = recipient
    const notificationData = {
      user_id: safeRecipientId, // Using recipient ID for RLS
      sender_id: safeSenderId, // Actual sender
      receiver_id: safeRecipientId, // Recipient
      workspace_id: workspaceId,
      type: "workspace_invitation",
      content,
      status: "pending",
      is_read: false,
    };

    // Get admin client to bypass RLS policies
    const admin = await getAdminClient();

    // Insert the notification using admin client to bypass RLS
    const { data: notification, error } = await admin
      .from("notifications")
      .insert([notificationData])
      .select()
      .single();

    if (error) {
      // Handle specific error cases
      if (error.code === "22P02") {
        console.error("JSON parse error:", error, content);
        throw new Error(`Invalid data format: ${error.message}`);
      }
      console.error("Notification creation error:", error);
      throw error;
    }

    return notification;
  } catch (error) {
    console.error("Error creating workspace invitation:", error);
    throw error;
  }
}

/**
 * Get all users matching a search term
 *
 * @param {string} searchTerm - The term to search for in user profiles
 * @returns {Promise<Array>} - Array of matching user profiles
 */
export async function searchUsers(searchTerm) {
  try {
    if (!searchTerm || searchTerm.trim().length < 2) {
      return [];
    }

    // Search for users by full_name, display_name or email
    const { data, error } = await supabase
      .from("profiles")
      .select("id, full_name, display_name, email, avatar_url, settings")
      .or(
        `full_name.ilike.%${searchTerm}%, 
         email.ilike.%${searchTerm}%, 
         settings->profile->display_name.ilike.%${searchTerm}%`
      )
      .order("full_name", { ascending: true })
      .limit(10);

    if (error) throw error;

    // Process results to add display name from settings if needed
    const processedResults =
      data?.map((user) => {
        // Check if we need to pull display_name from settings
        if (!user.display_name && user.settings?.profile?.display_name) {
          user.display_name = user.settings.profile.display_name;
        }

        return user;
      }) || [];

    return processedResults;
  } catch (error) {
    console.error("Error searching users:", error);
    return [];
  }
}

/**
 * Check if a user has already been invited to a workspace
 *
 * @param {string} userId - User ID to check
 * @param {string} workspaceId - Workspace ID
 * @returns {Promise<boolean>} - True if user has pending or accepted invitation
 */
export async function hasExistingInvitation(userId, workspaceId) {
  try {
    // Ensure IDs are properly formatted
    const safeUserId = String(userId);
    const safeWorkspaceId = String(workspaceId);

    // Updated to use receiver_id
    const { data, error } = await supabase
      .from("notifications")
      .select("id")
      .eq("receiver_id", safeUserId)
      .eq("workspace_id", safeWorkspaceId)
      .eq("type", "workspace_invitation")
      .eq("status", "pending")
      .limit(1);

    if (error) throw error;

    // If we found any matching notifications, an invitation exists
    return data && data.length > 0;
  } catch (error) {
    console.error("Error checking existing invitation:", error);
    return false;
  }
}

/**
 * Get workspace members and pending invitations
 *
 * @param {string} workspaceId - The workspace ID to check
 * @returns {Promise<object>} - Object containing members and pendingInvites arrays
 */
export async function getWorkspaceInvitationStatus(workspaceId) {
  try {
    // Ensure workspaceId is properly formatted
    const safeWorkspaceId = String(workspaceId);

    // Get workspace members
    const { data: workspaceData, error: workspaceError } = await supabase
      .from("workspaces")
      .select("workspace_members")
      .eq("id", safeWorkspaceId)
      .single();

    if (workspaceError) throw workspaceError;

    // Get all pending invitations for this workspace
    // Updated to use the receiver_id field
    const { data: pendingInvitesRaw, error: invitesError } = await supabase
      .from("notifications")
      .select(
        `
        id, 
        user_id, 
        sender_id, 
        receiver_id, 
        workspace_id, 
        created_at, 
        status
      `
      )
      .eq("type", "workspace_invitation")
      .eq("status", "pending")
      .eq("workspace_id", safeWorkspaceId);

    if (invitesError) throw invitesError;

    // Manually fetch the profile data for the recipients (stored in receiver_id)
    let pendingInvites = [];
    if (pendingInvitesRaw && pendingInvitesRaw.length > 0) {
      // Extract all recipient IDs
      const recipientIds = pendingInvitesRaw.map(
        (invite) => invite.receiver_id
      );

      // Fetch profiles for these recipients
      const { data: recipientProfiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, email, avatar_url, settings")
        .in("id", recipientIds);

      if (profilesError) throw profilesError;

      // Process profiles to extract display name from settings
      const processedProfiles = (recipientProfiles || []).map((profile) => ({
        ...profile,
        display_name: profile.settings?.profile?.display_name || "",
      }));

      // Map profiles to invitations
      pendingInvites = pendingInvitesRaw.map((invite) => {
        const profile = processedProfiles.find(
          (p) => p.id === invite.receiver_id
        );
        return {
          ...invite,
          profiles: profile || null,
        };
      });
    }

    // Get detailed profile info for existing members
    const memberIds = workspaceData?.workspace_members || [];
    let members = [];

    if (memberIds.length > 0) {
      const { data: membersData, error: membersError } = await supabase
        .from("profiles")
        .select("id, full_name, email, avatar_url, settings")
        .in("id", memberIds);

      if (membersError) throw membersError;

      // Process profile data to extract display name from settings
      members = (membersData || []).map((member) => ({
        ...member,
        display_name: member.settings?.profile?.display_name || "",
      }));
    }

    return {
      members,
      pendingInvites: pendingInvites || [],
    };
  } catch (error) {
    console.error("Error getting workspace invitation status:", error);
    return { members: [], pendingInvites: [] };
  }
}

/**
 * Accept a workspace invitation and add user to workspace members
 *
 * @param {string} notificationId - ID of the invitation notification
 * @param {string} userId - ID of the user accepting the invitation
 * @returns {Promise<object>} - Updated workspace and notification data
 */
export async function acceptWorkspaceInvitation(notificationId, userId) {
  try {
    // Ensure IDs are properly formatted
    const safeNotificationId = String(notificationId);
    const safeUserId = String(userId);

    // Updated to use receiver_id
    const { data: notification, error: notificationError } = await supabase
      .from("notifications")
      .select(
        "id, user_id, sender_id, receiver_id, workspace_id, content, status"
      )
      .eq("id", safeNotificationId)
      .eq("receiver_id", safeUserId) // Use receiver_id for recipient
      .eq("type", "workspace_invitation")
      .single();

    if (notificationError) throw notificationError;

    if (!notification) {
      throw new Error("Invitation not found or doesn't belong to this user");
    }

    if (notification.status !== "pending") {
      throw new Error(`Invitation already ${notification.status}`);
    }

    // Get workspace ID from dedicated field, fallback to content for compatibility
    const workspaceId =
      notification.workspace_id || notification.content.workspace_id;

    // Check if the workspace exists
    const { data: workspace, error: workspaceError } = await supabase
      .from("workspaces")
      .select("id, workspace_members, metadata, workspace_threads")
      .eq("id", workspaceId)
      .single();

    if (workspaceError) throw workspaceError;

    // Add user to workspace_members array if not already there
    const currentMembers = workspace.workspace_members || [];

    // Use admin client for all DB operations to bypass RLS
    const admin = getAdminClient();

    // Check if user is already a member
    if (currentMembers.includes(safeUserId)) {
      // User is already a member, just update notification status
      const { data: updatedNotification, error: updateError } = await admin
        .from("notifications")
        .update({ status: "accepted", updated_at: new Date().toISOString() })
        .eq("id", safeNotificationId)
        .select()
        .single();

      if (updateError) throw updateError;

      // Even if user is already a member, still add to user_workspaces
      await updateUserWorkspaces(safeUserId, workspaceId);

      // Check if user exists in workspace_members table
      const { data: existingMember } = await admin
        .from("workspace_members")
        .select("id")
        .eq("user_id", safeUserId)
        .eq("workspace_id", workspaceId)
        .maybeSingle();

      // If the user is not in workspace_members table, add them
      if (!existingMember) {
        const { error: memberError } = await admin
          .from("workspace_members")
          .insert({
            user_id: safeUserId,
            workspace_id: workspaceId,
            role: "member",
            permissions: {
              workspace: {
                edit: false,
                delete: false,
                invite_members: true,
                manage_roles: false,
              },
              files: {
                upload: true,
                delete: false,
                share: true,
              },
              memories: {
                create: true,
                edit: true,
                delete: false,
              },
              settings: {
                view: true,
                edit: false,
              },
            },
          });

        if (memberError) {
          console.error(
            "Error adding user to workspace_members table:",
            memberError
          );
        }
      }

      // Refresh the user profile in the auth store
      try {
        await useAuthStore.getState().refreshUserProfile();
      } catch (refreshError) {
        console.error("Error refreshing user profile:", refreshError);
        // Continue despite error
      }

      return {
        workspace,
        notification: updatedNotification,
        alreadyMember: true,
      };
    }

    // Add user to members array
    const updatedMembers = [...currentMembers, safeUserId];

    // Update metadata.stats.member_count
    const updatedMetadata = {
      ...(workspace.metadata || {}),
      stats: {
        ...(workspace.metadata?.stats || {}),
        member_count: updatedMembers.length,
      },
    };

    // CRITICAL CHANGE: Only update membership-related fields
    // without affecting workspace_threads or other content
    const { data: updatedWorkspace, error: updateWorkspaceError } = await admin
      .from("workspaces")
      .update({
        workspace_members: updatedMembers,
        metadata: updatedMetadata,
        updated_at: new Date().toISOString(),
        // DO NOT update workspace_threads or other content fields
      })
      .eq("id", workspaceId)
      .select("id, workspace_members, metadata")
      .single();

    if (updateWorkspaceError) throw updateWorkspaceError;

    // Add user to workspace_members join table
    const { error: memberError } = await admin
      .from("workspace_members")
      .insert({
        user_id: safeUserId,
        workspace_id: workspaceId,
        role: "member",
        // Include appropriate permissions
        permissions: {
          workspace: {
            edit: false,
            delete: false,
            invite_members: true,
            manage_roles: false,
          },
          files: {
            upload: true,
            delete: false,
            share: true,
          },
          memories: {
            create: true,
            edit: true,
            delete: false,
          },
          settings: {
            view: true,
            edit: false,
          },
        },
      });

    if (memberError) {
      console.error(
        "Error adding user to workspace_members table:",
        memberError
      );
    }

    // Update notification status
    const { data: updatedNotification, error: updateNotificationError } =
      await admin
        .from("notifications")
        .update({ status: "accepted", updated_at: new Date().toISOString() })
        .eq("id", safeNotificationId)
        .select()
        .single();

    if (updateNotificationError) throw updateNotificationError;

    // Add the workspace to the user's profile user_workspaces array
    await updateUserWorkspaces(safeUserId, workspaceId);

    // Refresh the user profile in the auth store
    try {
      await useAuthStore.getState().refreshUserProfile();
    } catch (refreshError) {
      console.error("Error refreshing user profile:", refreshError);
      // Continue despite error
    }

    // IMPORTANT: Merge the updatedWorkspace with the original workspace
    // to preserve threads and other content
    const mergedWorkspace = {
      ...workspace,
      workspace_members: updatedMembers,
      metadata: updatedMetadata,
    };

    return {
      workspace: mergedWorkspace, // Return the merged workspace to preserve all data
      notification: updatedNotification,
      alreadyMember: false,
    };
  } catch (error) {
    console.error("Error accepting workspace invitation:", error);
    throw error;
  }
}

/**
 * Helper function to update the user's profile user_workspaces array
 *
 * @param {string} userId - The user ID
 * @param {string} workspaceId - The workspace ID to add
 * @returns {Promise<void>}
 */
async function updateUserWorkspaces(userId, workspaceId) {
  try {
    // Ensure IDs are properly formatted
    const safeUserId = String(userId);
    const safeWorkspaceId = String(workspaceId);

    // Use admin client to bypass RLS policies
    const admin = getAdminClient();

    // Get the current user profile
    const { data: userProfile, error: profileError } = await admin
      .from("profiles")
      .select("user_workspaces, settings")
      .eq("id", safeUserId)
      .single();

    if (profileError) {
      console.error("Error fetching user profile:", profileError);
      throw profileError;
    }

    if (!userProfile) {
      console.error(`User profile not found for user ID: ${safeUserId}`);
      throw new Error("User profile not found");
    }

    // Parse existing user_workspaces or initialize as empty array
    let userWorkspaces = [];

    if (userProfile.user_workspaces) {
      // If it's a string that looks like an array, parse it
      if (
        typeof userProfile.user_workspaces === "string" &&
        userProfile.user_workspaces.startsWith("[") &&
        userProfile.user_workspaces.endsWith("]")
      ) {
        try {
          userWorkspaces = JSON.parse(userProfile.user_workspaces);
        } catch (e) {
          console.error("Error parsing user_workspaces:", e);
          userWorkspaces = [];
        }
      } else if (Array.isArray(userProfile.user_workspaces)) {
        // If it's already an array, use it
        userWorkspaces = userProfile.user_workspaces;
      }
    }

    // Check if the workspace ID is already in the array
    if (!userWorkspaces.includes(safeWorkspaceId)) {
      // Add the workspace ID to the array
      userWorkspaces.push(safeWorkspaceId);

      // Update the user's profile with the new user_workspaces array
      const { data: updatedProfile, error: updateError } = await admin
        .from("profiles")
        .update({
          user_workspaces: userWorkspaces,
          updated_at: new Date().toISOString(),
        })
        .eq("id", safeUserId)
        .select("user_workspaces");

      if (updateError) {
        console.error("Error updating user profile:", updateError);
        throw updateError;
      }

      // Also update the auth store
      try {
        await useAuthStore.getState().refreshUserProfile();
      } catch (refreshError) {
        console.error(
          "Error refreshing auth store after updating user_workspaces:",
          refreshError
        );
        // Continue despite error
      }
    }
  } catch (error) {
    console.error("Error updating user_workspaces:", error);
  }
}

// Export the updateUserWorkspaces function so it can be used by other components
export { updateUserWorkspaces };

/**
 * Decline a workspace invitation
 *
 * @param {string} notificationId - ID of the invitation notification
 * @param {string} userId - ID of the user declining the invitation
 * @returns {Promise<object>} - Updated notification
 */
export async function declineWorkspaceInvitation(notificationId, userId) {
  try {
    // Ensure IDs are properly formatted
    const safeNotificationId = String(notificationId);
    const safeUserId = String(userId);

    // Updated to use receiver_id
    const { data: notification, error: notificationError } = await supabase
      .from("notifications")
      .select("id, user_id, receiver_id, status")
      .eq("id", safeNotificationId)
      .eq("receiver_id", safeUserId) // Use receiver_id for recipient
      .eq("type", "workspace_invitation")
      .single();

    if (notificationError) throw notificationError;

    if (!notification) {
      throw new Error("Invitation not found or doesn't belong to this user");
    }

    if (notification.status !== "pending") {
      throw new Error(`Invitation already ${notification.status}`);
    }

    // Get admin client to bypass RLS
    const admin = getAdminClient();

    // Update notification status to declined
    const { data: updatedNotification, error: updateError } = await admin
      .from("notifications")
      .update({
        status: "declined",
        updated_at: new Date().toISOString(),
      })
      .eq("id", safeNotificationId)
      .select()
      .single();

    if (updateError) throw updateError;

    return updatedNotification;
  } catch (error) {
    console.error("Error declining workspace invitation:", error);
    throw error;
  }
}

/**
 * Subscribe to real-time notifications for a specific user
 *
 * @param {string} userId - The user ID to subscribe notifications for
 * @param {function} onNotificationInsert - Callback when a notification is created
 * @param {function} onNotificationUpdate - Callback when a notification is updated
 * @param {function} onNotificationDelete - Callback when a notification is deleted
 * @returns {object} - The subscription channel that can be unsubscribed
 */
export async function subscribeToUserNotifications(
  userId,
  onNotificationInsert = () => {},
  onNotificationUpdate = () => {},
  onNotificationDelete = () => {}
) {
  try {
    if (!userId) throw new Error("User ID is required for subscription");

    // Check if supabase connection is available
    if (!supabase || !supabase.realtime) {
      console.error("Supabase or realtime not available");
      return null;
    }

    // Create a unique channel name with timestamp to avoid conflicts
    const channelName = `notification:${userId}:${Date.now()}:${Math.random().toString(36).substring(2, 9)}`;

    console.debug(`Creating notification channel: ${channelName}`);

    try {
      // Authenticate for real-time (required for private channels)
      await supabase.realtime.setAuth();
    } catch (authError) {
      console.warn("Realtime auth error:", authError);
      // Continue anyway as it might still work
    }

    // Create channel with postgres_changes instead of broadcast for better reliability
    const channel = supabase
      .channel(channelName, {
        config: {
          private: false, // Use public channel to avoid auth issues
          presence: { key: userId },
        },
      })
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          try {
            console.debug("Notification INSERT:", payload);
            onNotificationInsert(payload);
          } catch (err) {
            console.debug("Error in notification insert handler:", err);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          try {
            console.debug("Notification UPDATE:", payload);
            onNotificationUpdate(payload);
          } catch (err) {
            console.debug("Error in notification update handler:", err);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          try {
            console.debug("Notification DELETE:", payload);
            onNotificationDelete(payload);
          } catch (err) {
            console.debug("Error in notification delete handler:", err);
          }
        }
      )
      .subscribe((status) => {
        console.debug(
          `Notification subscription status for ${channelName}: ${status}`
        );
        if (status === "CHANNEL_ERROR") {
          // CHANNEL_ERROR can occur when connection closes, which is normal
          // Only log as warning, not error, to reduce noise in console
          console.warn(
            `Notification subscription connection closed for channel ${channelName}. This is normal and may reconnect automatically.`
          );
        } else if (status === "TIMED_OUT") {
          // Timeout is also somewhat expected in long-running connections
          console.warn(
            `Notification subscription timed out for channel ${channelName}. Connection may reconnect automatically.`
          );
        } else if (status === "CLOSED") {
          console.debug(
            `Notification subscription closed for channel ${channelName}`
          );
        } else if (status === "SUBSCRIBED") {
          console.debug(
            `Notification subscription active for channel ${channelName}`
          );
        }
      });

    return channel;
  } catch (error) {
    console.error("Error subscribing to user notifications:", error);
    return null; // Return null instead of throwing to avoid component errors
  }
}

/**
 * Unsubscribe from a notification channel
 *
 * @param {object} channel - The channel returned from subscribeToUserNotifications
 */
export function unsubscribeFromNotifications(channel) {
  if (!channel) return;

  try {
    // Check if channel exists and has a valid unsubscribe method
    if (typeof channel.unsubscribe === "function") {
      // Try to unsubscribe using the channel's own method first
      channel.unsubscribe();
    } else if (supabase && typeof supabase.removeChannel === "function") {
      // Fallback to supabase.removeChannel if available
      // Only try to remove if channel is not already closed
      if (channel.state !== "CLOSED") {
        supabase.removeChannel(channel);
      }
    }
  } catch (error) {
    // Silently handle errors during unsubscription
    // This prevents the error from propagating and breaking the component
    console.debug(
      "Channel cleanup completed (may have been already closed):",
      error.message
    );
  }
}

/**
 * Notify all workspace members about a new thread
 *
 * @param {string} threadId - ID of the thread added to workspace
 * @param {string} workspaceId - ID of the workspace
 * @param {string} senderId - ID of the user who created/added the thread
 * @param {string} threadTitle - Title of the thread
 * @returns {Promise<boolean>} - Success status
 */
export async function notifyWorkspaceMembersAboutThread(
  threadId,
  workspaceId,
  senderId,
  threadTitle
) {
  try {
    // Ensure IDs are properly formatted
    const safeThreadId = String(threadId);
    const safeWorkspaceId = String(workspaceId);
    const safeSenderId = String(senderId);

    // Get admin client to bypass RLS
    const admin = getAdminClient();

    // Get all workspace members except the sender
    const { data: members, error: membersError } = await admin
      .from("workspace_members")
      .select("user_id")
      .eq("workspace_id", safeWorkspaceId)
      .neq("user_id", safeSenderId);

    if (membersError) {
      console.error("Error fetching workspace members:", membersError);
      return false;
    }

    if (!members || members.length === 0) {
      return true; // No one to notify, but operation was successful
    }

    // Build the notification content
    const content = {
      thread_id: safeThreadId,
      workspace_id: safeWorkspaceId,
      thread_title: threadTitle || "New shared chat",
      action: "thread_added",
      timestamp: new Date().toISOString(),
    };

    // Create a notification for each member
    const notifications = members.map((member) => ({
      user_id: member.user_id, // For RLS
      sender_id: safeSenderId,
      receiver_id: member.user_id,
      workspace_id: safeWorkspaceId,
      type: "workspace_thread_added",
      content,
      status: "info",
      is_read: false,
    }));

    // Insert all notifications
    const { error: insertError } = await admin
      .from("notifications")
      .insert(notifications);

    if (insertError) {
      console.error("Error creating thread notifications:", insertError);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error notifying workspace members about thread:", error);
    return false;
  }
}
